import type { TransactionSignature, VersionedTransactionResponse } from "@solana/web3.js";
import type { ReactNode } from "react";

import { transactionErrorFromLog } from "../../../constants/regex";
import { notify } from "../../notifications";
import type { KnownError } from "./knownErrors";
import { knownPostSendErrors, knownPreSendErrors } from "./knownErrors";

export class StepTxnTimeoutError extends Error {
  readonly signature: string;

  constructor(message: string, signature: string) {
    super(message);
    this.message = message;
    this.name = "StepTxnTimeoutError";
    this.signature = signature;
  }
}

export const getErrorsForTransaction = (transactionResponse: VersionedTransactionResponse) => {
  const errors: string[] = [];

  if (transactionResponse?.meta?.logMessages) {
    for (const log of transactionResponse.meta.logMessages) {
      const matchedErrors = transactionErrorFromLog.exec(log);
      if (!matchedErrors) {
        continue;
      }
      for (const error of matchedErrors) {
        if (!error) {
          continue;
        }
        errors.push(error);
      }
    }
  }

  return errors;
};

/**
 * Handles transaction errors that happen before it is sent to the node. i.e. user rejections, unathorized wallets, and signature verification failure. Displays a friendly notification if the error is known.
 * @param notificationMessage Notification title to show. Defaults to "Something went wrong"
 */
export const handlePreSendTxnError = (err: Error, notificationMessage = "Something went wrong") => {
  let displayError: KnownError | undefined;
  for (const knownError of knownPreSendErrors) {
    if (err.message.toLowerCase().match(knownError.searchObject)) {
      displayError = knownError;
      break;
    }
  }

  // Default error message if no others can be found.
  let usedNotificationMessage = notificationMessage;
  let notificationDescription: ReactNode = err.message;
  let icon;

  // Get signature if present in error. `TransactionExpiredTimeoutError` and `StepTxnTimeoutError` provide this value, for example.
  const txId = err["signature"];

  if (displayError) {
    usedNotificationMessage = displayError.notificationMessage;
    notificationDescription = displayError.notificationDescription;
    icon = displayError.icon;
  }

  notify({
    type: "error",
    message: usedNotificationMessage,
    description: notificationDescription,
    txId,
    icon,
  });
};

/**
 * Used to be "sendErrorContent" but logic has changed slightly and moved here.
 */
export const sendPostSendTxnErrorContent = (
  txId: TransactionSignature,
  transactionResponse: VersionedTransactionResponse,
) => {
  const errors = getErrorsForTransaction(transactionResponse);

  let displayError: KnownError | undefined;
  for (const knownError of knownPostSendErrors) {
    if (displayError) {
      break;
    }
    for (const error of errors) {
      if (error.toLowerCase().match(knownError.searchObject)) {
        displayError = knownError;
        break;
      }
    }
  }

  let notificationMessage = "Something went wrong";
  let notificationDescription: ReactNode = "Please try again.";
  let icon: ReactNode;

  if (displayError) {
    notificationMessage = displayError.notificationMessage;
    notificationDescription = displayError.notificationDescription;
    icon = displayError.icon;
  }

  notify({
    message: notificationMessage,
    type: "error",
    txId,
    description: notificationDescription,
    icon,
  });
};
